import { queryOptions } from '@tanstack/react-query';
import { api, buildQueryString } from './api';
import type {
  AlertsResponse,
  AnimalDetail,
  AuditLogResponse,
  AuditQueryParams,
  ConfigEntry,
  CurrentRationsResponse,
  DashboardSummary,
  EventQueryParams,
  EventsResponse,
  HerdListResponse,
  QueryInventoryParams,
  QueryInventoryResult,
  SessionResponse,
  ViewDataResponse,
  ViewDetail,
  ViewListResponse,
} from './schemas';

interface LastFeedingResponse {
  feedings: Array<{ head_count: number; group: string }>;
}

export const sessionQuery = queryOptions({
  queryKey: ['session'],
  queryFn: () => api.get<SessionResponse>('/api/me'),
  retry: false,
  staleTime: 5 * 60 * 1000,
});

export const summaryQuery = queryOptions({
  queryKey: ['summary'],
  queryFn: () => api.get<DashboardSummary>('/api/summary'),
  staleTime: 30_000,
  refetchInterval: 60_000,
});

export const alertsQuery = queryOptions({
  queryKey: ['alerts'],
  queryFn: () => api.get<AlertsResponse>('/api/alerts'),
  staleTime: 60_000,
  refetchInterval: 120_000,
});

export const lastFeedingQuery = queryOptions({
  queryKey: ['feeding', 'last'],
  queryFn: () => api.get<LastFeedingResponse>('/api/feeding?limit=1'),
  staleTime: 5 * 60 * 1000,
});

export const currentRationsQuery = queryOptions({
  queryKey: ['rations', 'current'],
  queryFn: () => api.get<CurrentRationsResponse>('/api/ration/current'),
  staleTime: 5 * 60 * 1000,
});

export const herdBaseKey = ['herd'] as const;

export function herdQuery(params?: {
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}) {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.search) searchParams.set('search', params.search);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  if (params?.sort_by) searchParams.set('sort_by', params.sort_by);
  if (params?.sort_dir) searchParams.set('sort_dir', params.sort_dir);
  const qs = searchParams.toString();

  return queryOptions({
    queryKey: [...herdBaseKey, params ?? {}],
    queryFn: () => api.get<HerdListResponse>(`/api/herd${qs ? `?${qs}` : ''}`),
    staleTime: 60_000,
  });
}

// Animal detail (for slide-in panel)
export function animalDetailQuery(animalId: string) {
  return queryOptions({
    queryKey: ['herd', 'detail', animalId],
    queryFn: () => api.get<AnimalDetail>(`/api/herd/${animalId}`),
    enabled: !!animalId,
    staleTime: 30_000,
  });
}

// Inventory
export function inventoryQuery(params?: QueryInventoryParams) {
  const qs = params ? buildQueryString(params as Record<string, unknown>) : '';
  return queryOptions({
    queryKey: ['inventory', params ?? {}],
    queryFn: () => api.get<QueryInventoryResult>(`/api/inventory${qs ? `?${qs}` : ''}`),
    staleTime: 5 * 60 * 1000,
  });
}

// Audit log
export const auditBaseKey = ['audit'] as const;

export function auditQuery(params?: AuditQueryParams) {
  const qs = params ? buildQueryString(params as Record<string, unknown>) : '';
  return queryOptions({
    queryKey: [...auditBaseKey, params ?? {}],
    queryFn: () => api.get<AuditLogResponse>(`/api/audit${qs ? `?${qs}` : ''}`),
    staleTime: 30_000,
  });
}

// Config
export const configQuery = queryOptions({
  queryKey: ['config'],
  queryFn: () => api.get<{ entries: ConfigEntry[] }>('/api/config'),
  staleTime: 60_000,
});

// Events
export function eventsQuery(params?: EventQueryParams) {
  const qs = params ? buildQueryString(params as Record<string, unknown>) : '';
  return queryOptions({
    queryKey: ['events', params ?? {}],
    queryFn: () => api.get<EventsResponse>(`/api/events${qs ? `?${qs}` : ''}`),
    staleTime: 60_000,
  });
}

// Views
export const viewsQuery = queryOptions({
  queryKey: ['views'],
  queryFn: () => api.get<ViewListResponse>('/api/views'),
  staleTime: 60_000,
});

export const pinnedViewsQuery = queryOptions({
  queryKey: ['views', 'pinned'],
  queryFn: () => api.get<ViewListResponse>('/api/views?pinned_only=true'),
  staleTime: 60_000,
  refetchInterval: 5 * 60 * 1000,
});

export function viewDetailQuery(viewId: string) {
  return queryOptions({
    queryKey: ['views', 'detail', viewId],
    queryFn: () => api.get<ViewDetail>(`/api/views/${viewId}`),
    staleTime: 60_000,
  });
}

export function viewDataQuery(viewId: string, refreshInterval?: number | null) {
  return queryOptions({
    queryKey: ['views', 'data', viewId],
    queryFn: () => api.get<ViewDataResponse>(`/api/views/${viewId}/data`),
    staleTime: refreshInterval ? refreshInterval * 60 * 1000 : 5 * 60 * 1000,
    ...(refreshInterval ? { refetchInterval: refreshInterval * 60 * 1000 } : {}),
  });
}
